import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import UserFilesSpace from "./UserFilesSpace";
import UserDetailsSpace from "./UserDetailsSpace";
import { RiSafe2Line } from "react-icons/ri";
import { HiLockClosed } from "react-icons/hi";
import useAuth from "../../../hooks/useAuth";
import { usePermissions } from "../../../utils/permissions";
import { Cap, Level } from "../../../utils/rbacCatalog";

type TabType = "preferences" | "data";

interface UserEntityTabsProps {
  entity_identification: string;
  customerId: string;
}

const UserEntityTabs: React.FC<UserEntityTabsProps> = ({
  entity_identification,
  customerId,
}) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const permissions = usePermissions(user);
  const [activeTab, setActiveTab] = useState<TabType>("data");

  const isSelf = user.user_identification === customerId;
  const canSeeFiles = isSelf || permissions.can(Cap.USERS_FILES, Level.VIEW);
  const canSeeData = isSelf || permissions.can(Cap.USERS_DATA, Level.VIEW);

  useEffect(() => {
    if (activeTab === "data" && !canSeeData) {
      if (canSeeFiles) setActiveTab("preferences");
    } else if (activeTab === "preferences" && !canSeeFiles) {
      if (canSeeData) setActiveTab("data");
    }
  }, [canSeeData, canSeeFiles, activeTab]);

  if (!canSeeData && !canSeeFiles) return null;

  const activeSubline =
    activeTab === "data"
      ? t("dashboard.userEntityTabs.subline-details")
      : t("dashboard.userEntityTabs.subline-files");

  return (
    <div className="w-full">
      <div className="flex items-center gap-3 border-b border-gray-100 pb-3 mb-4">
        <h2 className="flex items-center gap-2 text-sm font-semibold text-gray-900">
          <RiSafe2Line className="shrink-0 text-blue-600" />
          {t("dashboard.userEntityTabs.title-user")}
        </h2>
      </div>

      <p className="mb-5 flex items-center gap-1.5 text-xs text-gray-500">
        <HiLockClosed className="h-3.5 w-3.5 shrink-0 text-gray-400" />
        <span>{t("dashboard.userEntityTabs.privacy-note")}</span>
      </p>

      <div className="border-b border-gray-200 mb-3 overflow-hidden">
        <nav className="-mb-px flex space-x-4 sm:space-x-8" aria-label="Tabs">
          {canSeeData && (
            <button
              onClick={() => setActiveTab("data")}
              className={`
              min-w-0 flex-1 sm:flex-none py-4 px-1 border-b-2 font-medium text-sm transition-colors
              ${
                activeTab === "data"
                  ? "border-blue-500 text-blue-600"
                  : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
              }
            `}
            >
              <span className="flex items-center gap-2 min-w-0">
                <RiSafe2Line
                  className={`text-lg shrink-0 ${
                    activeTab === "data" ? "text-blue-600" : "text-gray-400"
                  }`}
                />
                <span className="truncate">
                  {t("dashboard.userEntityTabs.tab-preferences")}
                </span>
              </span>
            </button>
          )}

          {canSeeFiles && (
            <button
              onClick={() => setActiveTab("preferences")}
              className={`
              min-w-0 flex-1 sm:flex-none py-4 px-1 border-b-2 font-medium text-sm transition-colors
              ${
                activeTab === "preferences"
                  ? "border-blue-500 text-blue-600"
                  : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
              }
            `}
            >
              <span className="flex items-center gap-2 min-w-0">
                <RiSafe2Line
                  className={`text-lg shrink-0 ${
                    activeTab === "preferences"
                      ? "text-blue-600"
                      : "text-gray-400"
                  }`}
                />
                <span className="truncate">
                  {t("dashboard.userEntityTabs.tab-data")}
                </span>
              </span>
            </button>
          )}
        </nav>
      </div>

      <p className="mb-6 text-xs text-gray-500">{activeSubline}</p>

      <div className="mt-6">
        {activeTab === "data" && canSeeData && (
          <UserDetailsSpace
            entity_identification={entity_identification}
            customerId={customerId}
          />
        )}
        {activeTab === "preferences" && canSeeFiles && (
          <UserFilesSpace customerId={customerId} />
        )}
      </div>
    </div>
  );
};

export default UserEntityTabs;
